import { useEffect, useRef, useCallback } from 'react';

interface UseAutoLogoutOptions {
  isAuthenticated: boolean;
  logout: () => Promise<void> | void;
  timeoutMinutes?: number;
}

/**
 * Custom hook to automatically logout user after inactivity
 * @param isAuthenticated - Whether the user is currently authenticated
 * @param logout - The logout function to call when timeout expires
 * @param timeoutMinutes - Timeout in minutes (defaults to env variable or 30 minutes)
 */
export const useAutoLogout = ({
  isAuthenticated,
  logout,
  timeoutMinutes,
}: UseAutoLogoutOptions) => {
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastActivityRef = useRef<number>(Date.now());

  // Get timeout from env variable or parameter (in minutes, convert to milliseconds)
  const getTimeoutMs = useCallback(() => {
    const envTimeout = import.meta.env.VITE_AUTO_LOGOUT_TIME;
    const timeout = timeoutMinutes || (envTimeout ? parseInt(envTimeout, 10) : 30);
    return timeout * 60 * 1000; // Convert minutes to milliseconds
  }, [timeoutMinutes]);

  // Reset the inactivity timer
  const resetTimer = useCallback(() => {
    if (!isAuthenticated) return;

    // Clear existing timer
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Update last activity time
    lastActivityRef.current = Date.now();

    // Set new timer
    const timeoutMs = getTimeoutMs();
    timeoutRef.current = setTimeout(() => {
      console.log('Auto-logout: User inactive, logging out...');
      logout();
    }, timeoutMs);
  }, [isAuthenticated, logout, getTimeoutMs]);

  // Handle user activity events
  useEffect(() => {
    if (!isAuthenticated) {
      // Clear timer if user is not authenticated
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      return;
    }

    // List of events that indicate user activity
    const activityEvents = [
      'mousedown',
      'mousemove',
      'keypress',
      'scroll',
      'touchstart',
      'click',
      'keydown',
    ];

    // Add event listeners
    activityEvents.forEach((event) => {
      document.addEventListener(event, resetTimer, true);
    });

    // Initialize timer
    resetTimer();

    // Cleanup function
    return () => {
      activityEvents.forEach((event) => {
        document.removeEventListener(event, resetTimer, true);
      });
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [isAuthenticated, resetTimer]);

  // Handle visibility change (when user switches tabs/windows)
  useEffect(() => {
    if (!isAuthenticated) return;

    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Tab is hidden, pause timer
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }
      } else {
        // Tab is visible again, check if we need to logout
        const timeSinceLastActivity = Date.now() - lastActivityRef.current;
        const timeoutMs = getTimeoutMs();

        if (timeSinceLastActivity >= timeoutMs) {
          // User has been inactive for too long, logout
          console.log('Auto-logout: User inactive while tab was hidden, logging out...');
          logout();
        } else {
          // Reset timer with remaining time
          const remainingTime = timeoutMs - timeSinceLastActivity;
          timeoutRef.current = setTimeout(() => {
            console.log('Auto-logout: User inactive, logging out...');
            logout();
          }, remainingTime);
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isAuthenticated, logout, getTimeoutMs]);
};

